<template>
  <div>
    <transition-group name="list" tag="div">
      <div v-for="(location, index) in managedLocations" :key="index" class="location-grid mb-2">
        <GoogleAdsTargetingElement
          :model-value="location"
          @update:model-value="(newVal) => updateLocation(index, newVal)"
          :provider-id="props.providerId"
          :allow-proximity="props.allowProximity" />
        <div style="align-self: flex-end">
          <LeadButton variant="outline-danger" @click="removeLocation(index)">
            <template #icon>
              <IconTrashFilled size="16" />
            </template>
          </LeadButton>
        </div>
      </div>
    </transition-group>
  </div>

  <div>
    <LeadButton variant="secondary" @click="addLocationEntry">
      {{ $t('btn.add_location') }}
    </LeadButton>
  </div>

  <div v-if="props.errors && Object.keys(props.errors).length > 0">
    <div v-for="(errorList, field) in props.errors" :key="field" class="mt-2">
      <FormErrorList :errors="errorList" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import GoogleAdsTargetingElement from './GoogleAdsTargetingElement.vue';
import type { LocationTargetingPlace, LocationTargetingPosition } from '@/types/locationTargeting';
import LeadButton from '../generic/LeadButton.vue';
import { IconTrashFilled } from '@tabler/icons-vue';
import { FormErrorList } from '@prospective/lithium';
import translator from '@/locale/translator';

export interface LocationTargetingValue {
  target_locations: LocationTargetingPlace[];
  target_proximity_locations: LocationTargetingPosition[];
}

const props = withDefaults(
  defineProps<{
    modelValue: LocationTargetingValue;
    providerId: string | number;
    errors?: Record<string, string[]>;
    allowProximity?: boolean;
  }>(),
  {
    modelValue: () => ({ target_locations: [], target_proximity_locations: [] }),
    errors: () => ({}),
    allowProximity: true,
  }
);

const emit = defineEmits(['update:modelValue']);

// --- COMPUTED PROPERTIES ---
const managedLocations = computed({
  get: (): (LocationTargetingPlace | LocationTargetingPosition | null)[] => {
    // without proximity support the stored positions are never pushed, so they are not listed either
    const places = props.modelValue.target_locations || [];
    const positions = props.allowProximity ? props.modelValue.target_proximity_locations || [] : [];

    // placeholders (target_location_id === 0) are always rendered last so a newly added
    // empty row sits directly above the "Add location" button, below any existing rows.
    const placeholderPlaces = places.filter((p) => p.target_location_id === 0);
    const realPlaces = places.filter((p) => p.target_location_id !== 0);

    return [...realPlaces, ...positions, ...placeholderPlaces];
  },
  set: (newList: (LocationTargetingPlace | LocationTargetingPosition | null)[]) => {
    const places: LocationTargetingPlace[] = [];
    const positions: LocationTargetingPosition[] = [];

    newList.forEach((location) => {
      if (location) {
        if ('target_location_id' in location) {
          places.push(location);
        } else {
          positions.push(location as LocationTargetingPosition);
        }
      }
    });

    emit('update:modelValue', {
      target_locations: places,
      target_proximity_locations: positions,
    });
  },
});

// --- METHODS ---
const addLocationEntry = () => {
  const newPlaceholder: LocationTargetingPlace = {
    target_location_label: translator.get('campaigns.please_select_location'),
    target_location_id: 0,
    target_country_label: '',
    target_country_code: '',
    bid_modifier: 1.0,
  };
  managedLocations.value = [...managedLocations.value, newPlaceholder];
};

/**
 * @param index The index of the item to update.
 * @param newLocation The new location object from the GeoLocationSearch component.
 */
const updateLocation = (index: number, newLocation: LocationTargetingPlace | LocationTargetingPosition) => {
  const newList = [...managedLocations.value];
  newList[index] = newLocation;
  managedLocations.value = newList;
};

/**
 * @param index The index of the item to remove.
 */
const removeLocation = (index: number) => {
  const newList = [...managedLocations.value];
  newList.splice(index, 1);
  managedLocations.value = newList;
};
</script>

<style>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
.location-grid {
  display: grid;
  grid-template-columns: 5fr 1fr;
  gap: 8px;
}

.left-container > div {
  width: 100%;
}
</style>
